import { writable } from 'svelte/store';
import type { User } from '@supabase/supabase-js';
import { supabase } from '$lib/supabase';

interface AuthState {
  user: User | null;
  loading: boolean;
}

export const authStore = writable<AuthState>({
  user: null,
  loading: true
});

export function setAuthState(user: User | null, loading: boolean) {
  console.log('Setting auth state:', { user: !!user, loading });
  authStore.set({ user, loading });
}

export async function refreshSession() {
  console.log('Refreshing session...');
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('Error refreshing session:', error);
      setAuthState(null, false);
      return null;
    } else {
      console.log('Session refreshed successfully');
      setAuthState(user, false);
      return user;
    }
  } catch (err) {
    console.error('Exception during session refresh:', err);
    setAuthState(null, false);
    return null;
  }
}

export async function initializeAuth() {
  console.log('Initializing auth...');
  authStore.set({ user: null, loading: true });
  
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session retrieval error:', sessionError);
      setAuthState(null, false);
      return null;
    }
    
    if (!session) {
      console.log('No active session found');
      setAuthState(null, false);
      return null;
    }
    
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('User retrieval error:', userError);
      setAuthState(null, false);
      return null;
    }
    
    console.log('Auth initialized successfully', { hasUser: !!user });
    setAuthState(user, false);
    return user;
  } catch (err) {
    console.error('Exception during auth initialization:', err);
    setAuthState(null, false);
    return null;
  }
}

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      return false;
    }
    setAuthState(null, false);
    return true;
  } catch (err) {
    console.error('Exception during sign out:', err);
    return false;
  }
}